import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";

/**
 * Two Doors — collapsed state. The compact, in-flow module that replaces the
 * old floating "Gauge your needs" tab AND the mobile launcher. It lives inside
 * the page title section (never fixed/sticky), stays subordinate to the title,
 * and reopens the full experience — resuming unfinished progress or re-showing a
 * completed recommendation, per the state persisted in sessionStorage.
 *
 * Props:
 *   visible   — whether to render (mirrors the hook's showTab)
 *   onOpen    — (entry?) => void  entry ∈ family|business|resume|result|undefined
 *   compact   — small single-control widget for the Aircraft/Management heroes,
 *               positioned by the page in the empty right space (desktop only).
 *   mobileTab — a fixed pill in the bottom-right corner, phones only (mirrors the
 *               spot of the old "Gauge your needs" button). Rendered on every page
 *               so the experience is always reopenable on mobile.
 */
const STATE_KEY = "steck_2d_state";

const readState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) || "null");
  } catch (e) {
    return null;
  }
};

const TwoDoorsCollapsed = ({ visible, onOpen, compact = false, mobileTab = false }) => {
  if (!visible) return null;

  const st = readState() || {};
  const completed = !!st.completed;
  const inProgress = !completed && st.screen && st.screen !== "door";
  const entry = completed ? "result" : inProgress ? "resume" : undefined;

  const startOver = () => {
    try {
      sessionStorage.removeItem(STATE_KEY);
    } catch (e) {
      /* ignore */
    }
    onOpen();
  };

  // Mobile fixed tab — a small pill pinned to the bottom-right, phones only,
  // where the old "Gauge your needs" button lived. Always reopenable on mobile.
  if (mobileTab) {
    const label = completed ? "Your recommendation" : inProgress ? "Resume" : "Why owners fly";
    return (
      <button
        type="button"
        className="tdc-mtab"
        onClick={() => onOpen(entry)}
        aria-label="Open the Two Doors experience"
        data-testid="two-doors-mobile-tab"
      >
        <span>{label}</span>
        <ArrowUpRight size={15} strokeWidth={2.2} aria-hidden />
        <style>{`
          .tdc-mtab { --tdc-gold: #c8a46a; --tdc-gold-soft: #e2c894; display: none; }
          @media (max-width: 767px) {
            .tdc-mtab {
              position: fixed; z-index: 82;
              right: clamp(14px,4vw,20px);
              bottom: max(clamp(16px,3vh,24px), calc(env(safe-area-inset-bottom,0px) + 14px));
              display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
              padding: 11px 16px; border-radius: 999px;
              background: linear-gradient(180deg, var(--tdc-gold-soft) 0%, var(--tdc-gold) 100%);
              color: #14100a; border: 1px solid rgba(226,200,148,0.6);
              font-family: 'Inter', system-ui, sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 0.01em;
              box-shadow: 0 14px 34px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(200,164,106,0.25);
            }
            .tdc-mtab:active { transform: translateY(1px); }
            .tdc-mtab:focus-visible { outline: 2px solid var(--tdc-gold); outline-offset: 3px; }
          }
        `}</style>
      </button>
    );
  }

  // Compact widget — a small single-control tab used on the Aircraft/Management
  // pages, positioned in the hero's empty space by the page. Deliberately tiny
  // so it never covers other content, and it scrolls away with the page.
  if (compact) {
    const cta = completed ? "View recommendation" : inProgress ? "Resume" : "Explore";
    return (
      <button
        type="button"
        className="tdc-tab"
        onClick={() => onOpen(entry)}
        data-testid="two-doors-collapsed"
      >
        <span className="tdc-tab__expand" aria-hidden>
          <ArrowUpRight size={14} strokeWidth={2} />
        </span>
        <span className="tdc-tab__eyebrow">Why owners fly</span>
        <span className="tdc-tab__line">
          Same aircraft. <i>Two kinds of freedom.</i>
        </span>
        <span className="tdc-tab__cta">
          {cta} <ArrowRight size={13} strokeWidth={2} aria-hidden />
        </span>
        <style>{`
          .tdc-tab {
            --tdc-gold: #c8a46a; --tdc-gold-soft: #e2c894;
            position: relative;
            display: flex; flex-direction: column; gap: 7px; align-items: flex-start;
            text-align: left; width: 232px; cursor: pointer;
            padding: 14px 16px; border-radius: 12px;
            border: 1px solid rgba(200,164,106,0.34);
            background:
              linear-gradient(180deg, rgba(255,255,255,0.045) 0%, transparent 24%),
              linear-gradient(180deg, rgba(16,22,32,0.66) 0%, rgba(9,13,20,0.78) 100%);
            box-shadow: 0 20px 50px -32px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05);
            -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
            transition: border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease;
          }
          .tdc-tab:hover { border-color: rgba(200,164,106,0.7); transform: translateY(-1px); }
          .tdc-tab:focus-visible { outline: 2px solid var(--tdc-gold); outline-offset: 3px; }
          /* Corner arrow signals the tab expands into the full experience. */
          .tdc-tab__expand {
            position: absolute; top: 12px; right: 12px; display: inline-flex;
            color: var(--tdc-gold); opacity: 0.7;
            transition: transform 220ms ease, opacity 220ms ease;
          }
          .tdc-tab:hover .tdc-tab__expand { transform: translate(2px,-2px); opacity: 1; }
          .tdc-tab__eyebrow {
            font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em;
            text-transform: uppercase; color: var(--tdc-gold);
          }
          .tdc-tab__line { font-family: 'Fraunces', Georgia, serif; font-size: 15px; line-height: 1.2; color: #fff; padding-right: 18px; }
          .tdc-tab__line i { font-style: italic; color: var(--tdc-gold-soft); }
          .tdc-tab__cta {
            display: inline-flex; align-items: center; gap: 6px; margin-top: 2px;
            font-size: 12px; font-weight: 600; letter-spacing: 0.02em; color: var(--tdc-gold-soft);
          }
        `}</style>
      </button>
    );
  }

  return (
    <div className="tdc" data-testid="two-doors-collapsed">
      <span className="tdc__eyebrow">Why owners fly</span>
      <p className="tdc__line">
        Same aircraft. <i>Two kinds of freedom.</i>
      </p>

      {completed ? (
        <>
          <button
            type="button"
            className="tdc__primary"
            onClick={() => onOpen("result")}
            data-testid="two-doors-collapsed-result"
          >
            View your recommendation <ArrowRight size={14} strokeWidth={2} aria-hidden />
          </button>
          <button type="button" className="tdc__restart" onClick={startOver}>
            Start over
          </button>
        </>
      ) : inProgress ? (
        <>
          <button
            type="button"
            className="tdc__primary"
            onClick={() => onOpen("resume")}
            data-testid="two-doors-collapsed-resume"
          >
            Pick up where you left off <ArrowRight size={14} strokeWidth={2} aria-hidden />
          </button>
          <button type="button" className="tdc__restart" onClick={startOver}>
            Start over
          </button>
        </>
      ) : (
        <div className="tdc__pills">
          <button
            type="button"
            className="tdc__pill"
            onClick={() => onOpen("family")}
            data-testid="two-doors-collapsed-family"
          >
            For the Family
          </button>
          <button
            type="button"
            className="tdc__pill"
            onClick={() => onOpen("business")}
            data-testid="two-doors-collapsed-business"
          >
            For Business
          </button>
        </div>
      )}

      <style>{`
        .tdc {
          --tdc-gold: #c8a46a; --tdc-gold-soft: #e2c894;
          width: 100%; max-width: 320px;
          padding: 22px 24px; border-radius: 15px;
          text-align: left;
          border: 1px solid rgba(200,164,106,0.34);
          background:
            linear-gradient(180deg, rgba(255,255,255,0.045) 0%, transparent 24%),
            linear-gradient(180deg, rgba(16,22,32,0.62) 0%, rgba(9,13,20,0.72) 100%);
          box-shadow: 0 30px 70px -40px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05);
          -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
        }
        .tdc__eyebrow {
          display: block; font-family: 'IBM Plex Mono', monospace; font-size: 10.5px;
          letter-spacing: 0.24em; text-transform: uppercase; color: var(--tdc-gold);
          margin-bottom: 12px;
        }
        .tdc__line {
          font-family: 'Fraunces', Georgia, serif; font-weight: 400;
          font-size: 22px; line-height: 1.16; color: #fff; margin: 0 0 18px;
        }
        .tdc__line i { font-style: italic; color: var(--tdc-gold-soft); }
        .tdc__primary {
          display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
          padding: 11px 18px; border-radius: 9px; width: 100%; justify-content: center;
          background: linear-gradient(180deg, var(--tdc-gold-soft) 0%, var(--tdc-gold) 100%);
          color: #14100a; border: 1px solid rgba(226,200,148,0.6);
          font-size: 13.5px; font-weight: 600; letter-spacing: 0.01em;
          transition: transform 200ms ease, box-shadow 200ms ease;
          box-shadow: 0 12px 30px -14px rgba(200,164,106,0.6);
        }
        .tdc__primary:hover { transform: translateY(-1px); }
        .tdc__primary:focus-visible { outline: 2px solid var(--tdc-gold); outline-offset: 3px; }
        .tdc__pills { display: flex; flex-direction: column; gap: 9px; }
        .tdc__pill {
          display: inline-flex; align-items: center; justify-content: space-between; cursor: pointer;
          padding: 12px 16px; border-radius: 9px;
          background: rgba(255,255,255,0.03); border: 1px solid rgba(200,164,106,0.34);
          color: #fff; font-size: 14px; letter-spacing: 0.005em;
          transition: border-color 200ms ease, background 200ms ease, color 200ms ease;
        }
        .tdc__pill::after {
          content: "→"; color: var(--tdc-gold); font-size: 15px;
          transition: transform 200ms ease;
        }
        .tdc__pill:hover { border-color: rgba(200,164,106,0.75); background: rgba(200,164,106,0.08); }
        .tdc__pill:hover::after { transform: translateX(3px); }
        .tdc__pill:focus-visible { outline: 2px solid var(--tdc-gold); outline-offset: 2px; }
        .tdc__restart {
          display: block; margin-top: 12px; background: none; border: none; cursor: pointer; padding: 2px;
          font-size: 12px; color: rgba(147,161,177,0.75); letter-spacing: 0.02em;
          transition: color 200ms ease;
        }
        .tdc__restart:hover { color: var(--tdc-gold-soft); }
        .tdc__restart:focus-visible { outline: 2px solid var(--tdc-gold); outline-offset: 2px; }

        @media (max-width: 767px) {
          .tdc { max-width: none; padding: 18px 20px; }
          .tdc__line { font-size: 20px; }
        }
      `}</style>
    </div>
  );
};

export default TwoDoorsCollapsed;
